import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import { getAnyFolderTreeIsLoading } from 'documents/store/folders/selectors';
import { getRolesById } from '../roles/selectors';

export const aclClass = {
  space: 1,
  folder: 2,
  document: 3,
  file: 4,
};

export const getPermissionTree = state => state.permissions.permissionTree;

export const getPermissionsIsLoading = state => state.permissions.isLoading;

export const getPermissions = state => state.permissions.permissions;

export const getAclSids = state => state.entities.aclSids;

export const getEditingRoleId = state => state.permissions.editingRoleId;

export const getEditingRole = createSelector([getRolesById, getEditingRoleId], (roles, id) => roles[id] || {});

export const getEditingSid = createSelector([getEditingRoleId, getAclSids], (roleId, aclSids) => {
  if (roleId) {
    const aclSid = Object.values(aclSids).find(aclSid => aclSid.sid === `ROLE_${roleId}`);
    if (aclSid) {
      return aclSid.id;
    }
  }
  return null;
});

// Loading indicators
// ================================================================

export const getSidsLoadingPermissions = state => state.permissions.sidsLoadingPermissions;

export const getEditingSidPermissionsIsLoading = createSelector(
  [getEditingSid, getSidsLoadingPermissions],
  (sid, sidsLoadingPermissions) => sidsLoadingPermissions.includes(parseInt(sid, 10)),
);

export const getObjectIdentitiesIsLoading = state => state.permissions.objectIdentitiesIsLoading;

export const getEntityToObjectIdentityIndexIsLoading = state => state.permissions.entityToObjectIdentityIndexIsLoading;

export const getManagePermissionsIsLoading = createSelector(
  [
    getEditingSidPermissionsIsLoading,
    getObjectIdentitiesIsLoading,
    getAnyFolderTreeIsLoading,
    getEntityToObjectIdentityIndexIsLoading,
  ],
  (
    editingSidPermissionsIsLoading,
    objectIdentitiesIsLoading,
    anyFolderTreeIsLoading,
    entityToObjectIdentityIndexIsLoading,
  ) =>
    editingSidPermissionsIsLoading ||
    objectIdentitiesIsLoading ||
    anyFolderTreeIsLoading ||
    entityToObjectIdentityIndexIsLoading,
);

// Object identities
// ================================================================

export const getAclObjectIdentitiesById = state => state.entities.aclObjectIdentities;

export const getEntityToObjectIdentityIndex = state => state.permissions.entityToObjectIdentityIndex;

const getObjectIdentityIdForEntityId = (state, aclClass, entityId) =>
  entityId && Object.keys(getEntityToObjectIdentityIndex(state)).length > 0
    ? getEntityToObjectIdentityIndex(state)[aclClass][entityId] || null
    : null;

export const getObjectIdentityId = (state, props) => getObjectIdentityIdForEntityId(state, aclClass.folder, props.id);

const getObjectIdentityIdFromProps = (state, props) => props.objectIdentityId || null;

// Permissions
// ================================================================

export const getPermissionsForObjectIdentityId = createSelector(
  [getPermissions, getObjectIdentityIdFromProps],
  (permissions, oid) => permissions[oid] || null,
);

const getInheritedPermissionsForObjectIdentityIdHelper = (
  permissions,
  aclObjectIdentitiesById,
  oid,
  sid,
  isInherited = false,
) => {
  if (!permissions || !aclObjectIdentitiesById || !oid) {
    return null;
  }
  // Permission for this objectIdentity exists
  if (permissions[oid]) {
    return { ...permissions[oid], isInherited };
  }
  // Check permission of parent objectIdentity
  const objectIdentity = aclObjectIdentitiesById[oid];
  if (objectIdentity.parentObject !== null) {
    return getInheritedPermissionsForObjectIdentityIdHelper(
      permissions,
      aclObjectIdentitiesById,
      objectIdentity.parentObject,
      sid,
      true,
    );
  }
  // No parent -> return default that denies everything
  return {
    objectIdentityId: parseInt(oid, 10),
    sid,
    canRead: false,
    canCreate: false,
    canWrite: false,
    canDelete: false,
    canReadDocument: false,
    canCreateDocument: false,
    canWriteDocument: false,
    canDeleteDocument: false,
    canAdminister: false,
    canShare: false,
    isInherited: true,
  };
};
export const getInheritedPermissionsForObjectIdentityId = createCachedSelector(
  [getPermissions, getAclObjectIdentitiesById, (state, oid) => oid, getEditingSid],
  (permissions, aclObjectIdentitiesById, oid, sid) =>
    getInheritedPermissionsForObjectIdentityIdHelper(permissions, aclObjectIdentitiesById, oid, sid),
)((permissions, aclObjectIdentitiesById, oid) => oid || 'loading'); // cache selector by oid.
// 'loading' is only a dummy to avoid errors before all oids are fully loaded
